import {
  Box,
  Button,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  LocationEntity,
  useAndroid,
  useRealtimeCaseUpdates,
} from '@peritasai/repository';
import { CaseEventFilters, RealtimeEventType } from '@peritasai/types';
import type { ReactNode } from 'react';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useContentTitleVisibility } from '../../../shared';
import {
  DiscoveryCard,
  HubStatusCard,
  discoveryContentWrapSx,
  formatDiscoveryHeader,
  useDiscovery,
} from '../../discovery';
import { useAssignToCaseData } from '../hooks';
import { useAssignToCaseService } from '../services/assign-to-case.service';
import type { AssignToCaseCase } from '../types/assign-to-case';
import {
  AssignToCaseStep,
  DISCOVERABLE_ASSET_TYPES,
} from '../types/assign-to-case';
import {
  clearStoredLocation,
  readStoredLocation,
  writeStoredLocation,
} from '../utils/assignToCaseStorage';
import { buildCaseSelectionLabels } from '../utils/caseSelectionLabels';
import { CaseListCard } from './CaseListCard';
import { ErrorCard } from './ErrorCard';
import { LoadingCard } from './LoadingCard';
import { LocationListCard } from './LocationListCard';
import {
  cancelSelectionButtonSx,
  contentWrapVariantSx,
  featurePageSelectedMobileHeaderSx,
  featurePageStandaloneTitleWrapSx,
  featurePageTitleCompactWrapSx,
  featurePageTitleTextSx,
  featurePageTitleWrapSx,
  pageContainerVariantSx,
  popupOverlaySx,
  selectedFlowLocationSx,
  selectedFlowTitleSx,
} from './styles';

export const AssignToCasePage = () => {
  const { t } = useTranslation(['periverse']);
  const contentTitleVisibility = useContentTitleVisibility();
  const theme = useTheme();
  const isDesktopSelectedFlow = useMediaQuery(theme.breakpoints.up('md'));
  const selectionLabels = buildCaseSelectionLabels(t);
  const initialStoredLocationRef = useRef<LocationEntity | null>(
    readStoredLocation()
  );
  const {
    step,
    setStep,
    locations,
    cases,
    visibleLocations,
    visibleCases,
    showAllLocations,
    setShowAllLocations,
    showAllCases,
    setShowAllCases,
    loadLocations,
    loadCases,
    refreshCases,
  } = useAssignToCaseData();
  const [selectedLocation, setSelectedLocation] =
    useState<LocationEntity | null>(null);
  const [selectedCase, setSelectedCase] = useState<AssignToCaseCase | null>(
    null
  );
  const [isBootstrappingStoredLocation, setIsBootstrappingStoredLocation] =
    useState(() => initialStoredLocationRef.current !== null);
  const assignToCaseService = useAssignToCaseService();
  const {
    detectedAssets,
    resetDiscoverySession,
    handleCancelDiscovery,
    handleToggleAsset,
    handleAssign,
    isAssignLoading,
  } = useDiscovery({
    enabled: step === AssignToCaseStep.LOADING_HUB,
    isSuccess: step === AssignToCaseStep.SUCCESS,
    selectedLocation,
    selectedCaseId: selectedCase?.id ?? null,
    discoverableAssetTypes: DISCOVERABLE_ASSET_TYPES,
    getAssetsForLocation: assignToCaseService.getAssetsForLocationAndChild,
    assignTrackedItemsBatch: assignToCaseService.assignTrackedItemsBatch,
    onHubOffline: () => setStep(AssignToCaseStep.HUB_OFFLINE),
    onAssignSuccess: () => setStep(AssignToCaseStep.SUCCESS),
    onAssignSettled: () => {
      setStep(AssignToCaseStep.SELECT_CASE);
      setSelectedCase(null);
      setShowAllCases(false);
    },
    onCancel: () => setStep(AssignToCaseStep.SELECT_CASE),
  });

  const { isAndroid, locationId } = useAndroid();
  const isDiscoveryStep =
    step === AssignToCaseStep.LOADING_HUB || step === AssignToCaseStep.SUCCESS;
  const hasMultipleHubs = locations.length > 1 && !isAndroid;
  const shouldRenderStandaloneAssignTitle =
    !isBootstrappingStoredLocation &&
    (step === AssignToCaseStep.LOADING_LOCATIONS ||
      step === AssignToCaseStep.ERROR_LOCATIONS ||
      step === AssignToCaseStep.SELECT_LOCATION ||
      step === AssignToCaseStep.ERROR_CASES);
  const shouldRenderMobileAssignRouteTitle =
    !isBootstrappingStoredLocation &&
    !isDesktopSelectedFlow &&
    (step === AssignToCaseStep.LOADING_CASES ||
      step === AssignToCaseStep.SELECT_CASE);

  const standaloneAssignLocationName =
    step === AssignToCaseStep.LOADING_CASES ||
    step === AssignToCaseStep.ERROR_CASES
      ? selectedLocation?.name ?? null
      : null;

  useLayoutEffect(() => {
    contentTitleVisibility?.setHideContentTitle(true);
    contentTitleVisibility?.setHideContentTitleOnDesktop(false);
  }, [contentTitleVisibility]);

  const renderAssignFeatureTitle = ({
    locationName,
    showDesktopTitle,
    mobileAction,
  }: {
    locationName?: string | null;
    showDesktopTitle: boolean;
    mobileAction?: ReactNode;
  }) => {
    const titleSx = locationName ? selectedFlowTitleSx : featurePageTitleTextSx;
    const titleWrapSx = locationName
      ? featurePageTitleCompactWrapSx
      : showDesktopTitle
      ? featurePageStandaloneTitleWrapSx
      : featurePageTitleWrapSx;
    return (
      <Box sx={titleWrapSx}>
        {mobileAction ? (
          <Box
            sx={{
              width: '100%',
              display: { xs: 'flex', md: 'none' },
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '16px',
            }}
          >
            <Typography component="h1" sx={titleSx}>
              {selectionLabels.featureTitle}
            </Typography>
            {mobileAction}
          </Box>
        ) : (
          <Typography
            component="h1"
            sx={{
              ...titleSx,
              display: { xs: 'block', md: 'none' },
            }}
          >
            {selectionLabels.featureTitle}
          </Typography>
        )}
        {showDesktopTitle && (
          <Typography
            component="p"
            sx={{
              ...titleSx,
              display: { xs: 'none', md: 'block' },
            }}
          >
            {selectionLabels.featureTitle}
          </Typography>
        )}
        {locationName && (
          <Typography sx={selectedFlowLocationSx}>{locationName}</Typography>
        )}
      </Box>
    );
  };

  useEffect(() => {
    if (!initialStoredLocationRef.current) return;
    if (
      step === AssignToCaseStep.SELECT_CASE ||
      step === AssignToCaseStep.ERROR_CASES ||
      step === AssignToCaseStep.ERROR_LOCATIONS
    ) {
      setIsBootstrappingStoredLocation(false);
    }
  }, [step]);

  const now = new Date();
  const startDate = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
    0,
    0,
    0,
    0
  ).toISOString();
  const endDate = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
    23,
    59,
    59,
    999
  ).toISOString();
  const caseEventTypes: RealtimeEventType[] = [
    RealtimeEventType.CASE_CREATED,
    RealtimeEventType.CASE_UPDATED,
    RealtimeEventType.CASE_CANCELLED,
    RealtimeEventType.CASE_DELETED,
  ];

  const caseFilter: CaseEventFilters = {
    startDate,
    endDate,
  };
  useRealtimeCaseUpdates({
    filters: caseFilter,
    eventTypes: caseEventTypes,
    enabled: step === AssignToCaseStep.SELECT_CASE && !!selectedLocation,
    onEvent: () => {
      if (!selectedLocation) return;
      void refreshCases(selectedLocation.id);
    },
  });

  const handleSelectLocation = useCallback(
    (location: LocationEntity) => {
      initialStoredLocationRef.current = null;
      setSelectedLocation(location);
      writeStoredLocation(location);
      setSelectedCase(null);
      setShowAllCases(false);
      resetDiscoverySession();
      setIsBootstrappingStoredLocation(false);
      void loadCases(location.id);
    },
    [loadCases, resetDiscoverySession, setShowAllCases]
  );

  useEffect(() => {
    const storedLocation = initialStoredLocationRef.current;
    const storedLocationId = storedLocation?.id ?? locationId;
    if (!storedLocationId) return;
    if (step !== AssignToCaseStep.SELECT_LOCATION) return;

    const matchedLocation = locations.find(
      (location) => location.id === storedLocationId
    );

    if (!matchedLocation) {
      clearStoredLocation();
      initialStoredLocationRef.current = null;
      setIsBootstrappingStoredLocation(false);
      return;
    }
    setSelectedLocation(matchedLocation);
    setSelectedCase(null);
    setShowAllCases(false);
    resetDiscoverySession();
    void loadCases(matchedLocation.id);
  }, [
    step,
    locationId,
    locations,
    setShowAllCases,
    loadCases,
    resetDiscoverySession,
  ]);

  const handleSelectCase = useCallback(
    (selected: AssignToCaseCase) => {
      resetDiscoverySession();
      setShowAllCases(false);
      setSelectedCase(selected);
      setStep(AssignToCaseStep.LOADING_HUB);
    },
    [resetDiscoverySession, setShowAllCases, setStep]
  );

  const handleCancelCaseSelection = useCallback(() => {
    resetDiscoverySession();
    clearStoredLocation();
    initialStoredLocationRef.current = null;
    setSelectedCase(null);
    setSelectedLocation(null);
    setShowAllCases(false);
    setIsBootstrappingStoredLocation(false);
    setStep(AssignToCaseStep.SELECT_LOCATION);
  }, [resetDiscoverySession, setShowAllCases, setStep]);

  const handleReturnToCaseSelection = useCallback(() => {
    resetDiscoverySession();
    setShowAllCases(false);
    setStep(AssignToCaseStep.SELECT_CASE);
  }, [resetDiscoverySession, setShowAllCases, setStep]);

  return (
    <Box
      sx={pageContainerVariantSx(
        isDiscoveryStep,
        step === AssignToCaseStep.SELECT_CASE && !!selectedLocation
      )}
    >
      <Box
        sx={{
          ...contentWrapVariantSx(isDiscoveryStep),
          ...(isBootstrappingStoredLocation
            ? { marginTop: { xs: '0px', md: '0px' } }
            : {}),
        }}
      >
        {shouldRenderStandaloneAssignTitle &&
          renderAssignFeatureTitle({
            locationName: standaloneAssignLocationName,
            showDesktopTitle: true,
          })}

        {shouldRenderMobileAssignRouteTitle && (
          <Box sx={featurePageSelectedMobileHeaderSx}>
            <Typography component="h1" sx={featurePageTitleTextSx}>
              {selectionLabels.featureTitle}
            </Typography>
            <Button
              variant="outlined"
              size="large"
              onClick={handleCancelCaseSelection}
              sx={cancelSelectionButtonSx}
            >
              {selectionLabels.changeLocationButtonLabel}
            </Button>
          </Box>
        )}

        {isBootstrappingStoredLocation && (
          <LoadingCard
            rows={5}
            titleWidth="35%"
            subtitleWidth="60%"
            cardWidth={{ xs: '450px', md: '100%' }}
            cardMaxWidth={{ xs: '450px', md: '100%' }}
          />
        )}

        {!isBootstrappingStoredLocation &&
          step === AssignToCaseStep.LOADING_LOCATIONS && (
            <LoadingCard
              rows={5}
              cardWidth={{ xs: '450px', md: '100%' }}
              cardMaxWidth={{ xs: '450px', md: '100%' }}
            />
          )}

        {!isBootstrappingStoredLocation &&
          step === AssignToCaseStep.ERROR_LOCATIONS && (
            <Box sx={popupOverlaySx}>
              <ErrorCard
                title="Something went wrong"
                description="The assignment station locations cannot be loaded."
                onCancel={() => setStep(AssignToCaseStep.SELECT_LOCATION)}
                onRetry={() => void loadLocations()}
              />
            </Box>
          )}

        {!isBootstrappingStoredLocation &&
          step === AssignToCaseStep.SELECT_LOCATION && (
            <LocationListCard
              locations={visibleLocations}
              showAll={showAllLocations}
              showViewAll={locations.length > 5}
              labels={selectionLabels}
              onToggleViewAll={() => setShowAllLocations((prev) => !prev)}
              onSelect={handleSelectLocation}
            />
          )}

        {!isBootstrappingStoredLocation &&
          step === AssignToCaseStep.LOADING_CASES && (
            <LoadingCard
              rows={5}
              titleWidth="35%"
              subtitleWidth="60%"
              cardWidth={{ xs: '450px', md: '100%' }}
              cardMaxWidth={{ xs: '450px', md: '100%' }}
            />
          )}

        {!isBootstrappingStoredLocation &&
          step === AssignToCaseStep.ERROR_CASES && (
            <Box sx={popupOverlaySx}>
              <ErrorCard
                title="Something went wrong"
                description="The case schedule for this location cannot be loaded."
                onCancel={() => setStep(AssignToCaseStep.SELECT_LOCATION)}
                onRetry={() =>
                  selectedLocation
                    ? void loadCases(selectedLocation.id)
                    : void loadLocations()
                }
              />
            </Box>
          )}

        {!isBootstrappingStoredLocation &&
          step === AssignToCaseStep.SELECT_CASE &&
          selectedLocation && (
            <CaseListCard
              cases={visibleCases}
              location={selectedLocation}
              hasSelectedLocation={!!selectedLocation}
              showEmbeddedPageTitle={isDesktopSelectedFlow}
              showAll={showAllCases}
              showViewAll={cases.length > 3}
              showCancel={hasMultipleHubs}
              labels={selectionLabels}
              onToggleViewAll={() => setShowAllCases((prev) => !prev)}
              onSelect={handleSelectCase}
              onCancel={handleCancelCaseSelection}
            />
          )}

        {step === AssignToCaseStep.HUB_OFFLINE && (
          <Box sx={popupOverlaySx}>
            <Box sx={{ width: '100%', maxWidth: '596px' }}>
              <HubStatusCard
                title="OR Assignment Station Offline"
                message="You cannot assign assets right now. Check this station is powered on and connected."
                onConfirm={handleReturnToCaseSelection}
              />
            </Box>
          </Box>
        )}

        {step === AssignToCaseStep.HUB_IN_USE && (
          <Box sx={popupOverlaySx}>
            <Box sx={{ width: '100%', maxWidth: '596px' }}>
              <HubStatusCard
                title="OR Assignment Station In Use"
                message="You cannot open this station while it is in use by another user."
                onConfirm={handleReturnToCaseSelection}
              />
            </Box>
          </Box>
        )}

        {(step === AssignToCaseStep.LOADING_HUB ||
          step === AssignToCaseStep.SUCCESS) &&
          selectedCase &&
          selectedLocation && (
            <Box sx={discoveryContentWrapSx}>
              <DiscoveryCard
                header={formatDiscoveryHeader({
                  locationName: selectedLocation.name,
                  timeLabel: selectedCase.timeLabel,
                  surgeonName: selectedCase.surgeonName,
                  procedureName: selectedCase.procedureName,
                  caseDisplayId: selectedCase.id,
                })}
                isSuccess={step === AssignToCaseStep.SUCCESS}
                isLoading={isAssignLoading}
                detectedAssets={detectedAssets}
                onCancel={() => {
                  setShowAllCases(false);
                  handleCancelDiscovery();
                }}
                onAssign={handleAssign}
                onToggleAsset={handleToggleAsset}
                copy={{
                  waitingTitle: 'Move Assets onto Station to Assign to Case',
                  searchingLabel: 'Searching for Assets',
                  detectedTitle: 'Assets Detected',
                  successTitle: 'Assets Assigned to Case',
                  assignLabel: 'Assign',
                }}
              />
            </Box>
          )}
      </Box>
    </Box>
  );
};
